import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Save, Send, CheckCircle2, Trash2, Plus,
  FileText, ScanLine, Download, DollarSign, AlertTriangle,
} from "lucide-react";

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-AU",
    { style: "currency", currency: "AUD" });

const STATUS_STYLE = {
  review: "bg-amber-50 text-amber-800 border-amber-200",
  pushed: "bg-sky-50 text-sky-800 border-sky-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const STATUS_LABEL = {
  review: "Needs review",
  pushed: "Pushed to Xero",
  paid: "Paid",
};

const emptyLine = () => ({
  _uid: `l_${Math.random().toString(36).slice(2, 10)}`,
  description: "", quantity: 1, unit_price: 0, gst: true,
  account_code: "", account_hint: "",
});

const stampUids = (lines) =>
  (lines || []).map((li) =>
    li._uid ? li : { ...li, _uid: `l_${Math.random().toString(36).slice(2, 10)}` });

function computeTotals(lines) {
  let sub = 0, gst = 0;
  for (const li of lines || []) {
    const amt = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
    sub += amt;
    if (li.gst) gst += amt * 0.1;
  }
  return { subtotal: sub, gst_total: gst, total: sub + gst };
}

export default function BillDetailPage() {
  const { billId } = useParams();
  const nav = useNavigate();
  const [bill, setBill] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [reocring, setReocring] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        api.get(`/bills/${billId}`),
        api.get("/bills/xero-tenants"),
      ]);
      setBill({ ...b.data, line_items: stampUids(b.data.line_items) });
      setTenants(t.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load bill");
    } finally {
      setLoading(false);
    }
  }, [billId]);
  useEffect(() => { load(); }, [load]);

  if (!bill) {
    return (
      <div className="p-8 flex items-center justify-center text-zinc-500">
        {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : "Bill not found"}
      </div>
    );
  }

  const totals = computeTotals(bill.line_items || []);
  const locked = bill.status === "pushed" || bill.status === "paid";

  const set = (patch) => setBill({ ...bill, ...patch });
  const updateLine = (i, patch) => {
    const lines = [...(bill.line_items || [])];
    lines[i] = { ...lines[i], ...patch };
    set({ line_items: lines });
  };
  const addLine = () =>
    set({ line_items: [...(bill.line_items || []), emptyLine()] });
  const removeLine = (i) => {
    const lines = [...(bill.line_items || [])];
    lines.splice(i, 1);
    set({ line_items: lines });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        supplier_name: bill.supplier_name || "Unknown supplier",
        supplier_email: bill.supplier_email || null,
        supplier_abn: bill.supplier_abn || null,
        invoice_number: bill.invoice_number || null,
        issue_date: bill.issue_date || null,
        due_date: bill.due_date || null,
        reference: bill.reference || null,
        notes: bill.notes || null,
        xero_tenant_id: bill.xero_tenant_id || null,
        xero_tenant_name:
          tenants.find((t) => t.tenant_id === bill.xero_tenant_id)?.tenant_name || null,
        ap_account_code: bill.ap_account_code || null,
        line_items: (bill.line_items || []).map((li) => ({
          description: li.description || "Line item",
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
          gst: !!li.gst,
          account_code: li.account_code || null,
          account_hint: li.account_hint || null,
        })),
        // Extended local-only fields — kept out of Xero, used for
        // the Recurring Bills page, email-ingest workflow, etc.
        // NOTE: `workflow_status` (not `status`) — the router
        // reserves `status` for its own AP pipeline (review/pushed/paid).
        workflow_status: bill.workflow_status || null,
        bill_type: bill.bill_type || null,
        assigned_to_pay_user_id: bill.assigned_to_pay_user_id || null,
        currency: bill.currency || "AUD",
        currency_rate: bill.currency_rate == null ? 1.0 : Number(bill.currency_rate),
        gst_amount: bill.gst_amount == null || bill.gst_amount === ""
          ? null : Number(bill.gst_amount),
        received_date: bill.received_date || null,
        payment_terms: bill.payment_terms || null,
        entity_to_be_invoiced: bill.entity_to_be_invoiced || null,
        po_number: bill.po_number || null,
        invoice_sent_to_client_no: bill.invoice_sent_to_client_no || null,
        frequency: bill.frequency || null,
        amount_type: bill.amount_type || null,
        payment_method: bill.payment_method || null,
        paid_date: bill.paid_date || null,
        paid_by_user_id: bill.paid_by_user_id || null,
      };
      const { data } = await api.patch(`/bills/${billId}`, body);
      setBill({ ...data, line_items: stampUids(data.line_items) });
      toast.success("Bill saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reocr = async () => {
    setReocring(true);
    try {
      const { data } = await api.post(`/bills/${billId}/reocr`);
      setBill({ ...data, line_items: stampUids(data.line_items) });
      toast.success("Re-extracted from the original document");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-OCR failed");
    } finally {
      setReocring(false);
    }
  };

  const push = async () => {
    if (!bill.xero_tenant_id) {
      toast.error("Choose a Xero organisation first");
      return;
    }
    if (!bill.ap_account_code &&
        !(bill.line_items || []).every((li) => li.account_code)) {
      toast.error("Set an AP account code (or one per line)");
      return;
    }
    setPushing(true);
    try {
      await api.post(`/bills/${billId}/push-to-xero`);
      toast.success("Pushed to Xero as ACCPAY bill");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed");
    } finally {
      setPushing(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this bill? The uploaded document will also be removed.")) return;
    try {
      await api.delete(`/bills/${billId}`);
      toast.success("Bill deleted");
      nav("/bills");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="bill-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav("/bills")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to bills
        </Button>
        <div className="flex-1" />
        <Badge variant="outline" className={STATUS_STYLE[bill.status] ||
              "bg-zinc-50 text-zinc-700 border-zinc-200"}>
          {STATUS_LABEL[bill.status] || bill.status}
        </Badge>
        {bill.confidence && (
          <Badge variant="outline"
            className={bill.confidence === "high"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : bill.confidence === "low"
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-zinc-50 text-zinc-700 border-zinc-200"}>
            OCR confidence: {bill.confidence}
          </Badge>
        )}
      </div>

      {bill.ocr?.error && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-3 flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">OCR reported an issue</div>
              <div className="text-xs">{bill.ocr.error}</div>
              <div className="text-xs mt-1">
                Review and correct the fields below, or press &quot;Re-run OCR&quot;.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="p-5 space-y-4">
            <div className="text-sm font-semibold text-zinc-700">Supplier</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Supplier name</Label>
                <Input value={bill.supplier_name || ""} disabled={locked}
                  onChange={(e) => set({ supplier_name: e.target.value })}
                  data-testid="bill-supplier-name" />
              </div>
              <div className="space-y-1">
                <Label>Supplier email</Label>
                <Input value={bill.supplier_email || ""} disabled={locked}
                  onChange={(e) => set({ supplier_email: e.target.value })}
                  data-testid="bill-supplier-email" />
              </div>
              <div className="space-y-1">
                <Label>Supplier ABN</Label>
                <Input value={bill.supplier_abn || ""} disabled={locked}
                  onChange={(e) => set({ supplier_abn: e.target.value })}
                  data-testid="bill-supplier-abn" />
              </div>
              <div className="space-y-1">
                <Label>Invoice number</Label>
                <Input value={bill.invoice_number || ""} disabled={locked}
                  onChange={(e) => set({ invoice_number: e.target.value })}
                  data-testid="bill-invoice-number" />
              </div>
              <div className="space-y-1">
                <Label>Issue date</Label>
                <Input type="date" value={bill.issue_date || ""} disabled={locked}
                  onChange={(e) => set({ issue_date: e.target.value })}
                  data-testid="bill-issue-date" />
              </div>
              <div className="space-y-1">
                <Label>Due date</Label>
                <Input type="date" value={bill.due_date || ""} disabled={locked}
                  onChange={(e) => set({ due_date: e.target.value })}
                  data-testid="bill-due-date" />
              </div>
              <div className="space-y-1">
                <Label>Reference</Label>
                <Input value={bill.reference || ""} disabled={locked}
                  onChange={(e) => set({ reference: e.target.value })}
                  data-testid="bill-reference" />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea value={bill.notes || ""} disabled={locked} rows={2}
                  onChange={(e) => set({ notes: e.target.value })}
                  data-testid="bill-notes" />
              </div>
            </div>

            {/* ── Local workflow metadata (not pushed to Xero) ── */}
            <div className="pt-2 border-t border-zinc-100" />
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-zinc-700">Local workflow &amp; recurring</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Set <span className="font-mono">Frequency</span> to any recurring value to make this bill appear on the Recurring Bills page.
                </div>
              </div>
              {bill.frequency && bill.frequency !== "one_off" && (
                <span className="text-[10px] rounded-sm border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 px-1.5 py-0.5 font-semibold">
                  Recurring · {bill.frequency}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Workflow status</Label>
                <select value={bill.workflow_status || ""} disabled={locked}
                  onChange={(e) => set({ workflow_status: e.target.value || null })}
                  className="h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="bill-workflow-status-select">
                  <option value="">—</option>
                  {["New", "In Progress", "Awaiting Approval", "Paid",
                    "Overdue", "Disputed", "On Hold"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <select value={bill.bill_type || ""} disabled={locked}
                  onChange={(e) => set({ bill_type: e.target.value || null })}
                  className="h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="bill-type-select">
                  <option value="">—</option>
                  {["Invoice", "Statement", "Subscription", "Notice",
                    "Renewal", "Utility", "Rent / Lease", "Other"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Frequency</Label>
                <select value={bill.frequency || ""} disabled={locked}
                  onChange={(e) => set({ frequency: e.target.value || null })}
                  className="h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="bill-frequency-select">
                  <option value="">—</option>
                  <option value="one_off">One-off</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="biennial">Biennial</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Amount type</Label>
                <select value={bill.amount_type || ""} disabled={locked}
                  onChange={(e) => set({ amount_type: e.target.value || null })}
                  className="h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="bill-amount-type-select">
                  <option value="">—</option>
                  <option value="fixed">Fixed</option>
                  <option value="variable">Variable</option>
                  <option value="estimate">Estimate</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Payment method</Label>
                <select value={bill.payment_method || ""} disabled={locked}
                  onChange={(e) => set({ payment_method: e.target.value || null })}
                  className="h-9 w-full rounded-sm border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="bill-payment-method-select">
                  <option value="">—</option>
                  {["Direct debit", "EFT", "Credit card", "BPAY",
                    "PayPal", "Cash", "Cheque", "Other"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Payment terms</Label>
                <Input value={bill.payment_terms || ""} disabled={locked}
                  onChange={(e) => set({ payment_terms: e.target.value })}
                  placeholder="e.g. Net 14, COD, EOM"
                  data-testid="bill-payment-terms" />
              </div>
              <div className="space-y-1">
                <Label>Received date</Label>
                <Input type="date" value={bill.received_date || ""} disabled={locked}
                  onChange={(e) => set({ received_date: e.target.value })}
                  data-testid="bill-received-date" />
              </div>
              <div className="space-y-1">
                <Label>Paid date</Label>
                <Input type="date" value={bill.paid_date || ""} disabled={locked}
                  onChange={(e) => set({ paid_date: e.target.value })}
                  data-testid="bill-paid-date" />
              </div>
              <div className="space-y-1">
                <Label>PO number</Label>
                <Input value={bill.po_number || ""} disabled={locked}
                  onChange={(e) => set({ po_number: e.target.value })}
                  data-testid="bill-po-number" />
              </div>
              <div className="space-y-1">
                <Label>Entity to be invoiced</Label>
                <Input value={bill.entity_to_be_invoiced || ""} disabled={locked}
                  onChange={(e) => set({ entity_to_be_invoiced: e.target.value })}
                  placeholder="Which of our entities pays this?"
                  data-testid="bill-entity-invoiced" />
              </div>
              <div className="space-y-1">
                <Label>Invoice sent to client (rebill #)</Label>
                <Input value={bill.invoice_sent_to_client_no || ""} disabled={locked}
                  onChange={(e) => set({ invoice_sent_to_client_no: e.target.value })}
                  placeholder="Rebill invoice number if on-charged"
                  data-testid="bill-rebill-no" />
              </div>
              <div className="space-y-1">
                <Label>GST amount</Label>
                <Input type="number" step="0.01" min="0"
                  value={bill.gst_amount ?? ""} disabled={locked}
                  onChange={(e) => set({ gst_amount: e.target.value })}
                  data-testid="bill-gst-amount" />
              </div>
              <div className="space-y-1">
                <Label>Currency</Label>
                <Input value={bill.currency || "AUD"} disabled={locked}
                  onChange={(e) => set({ currency: e.target.value.toUpperCase() })}
                  maxLength={3} className="uppercase"
                  data-testid="bill-currency" />
              </div>
              <div className="space-y-1">
                <Label>Currency rate</Label>
                <Input type="number" step="0.0001" min="0"
                  value={bill.currency_rate ?? 1}
                  disabled={locked || (bill.currency || "AUD") === "AUD"}
                  onChange={(e) => set({ currency_rate: e.target.value })}
                  data-testid="bill-currency-rate" />
              </div>
              <div className="space-y-1">
                <Label>Assigned to pay (user)</Label>
                <Input value={bill.assigned_to_pay_user_id || ""} disabled={locked}
                  onChange={(e) => set({ assigned_to_pay_user_id: e.target.value })}
                  placeholder="Team member email or id"
                  data-testid="bill-assigned-to-pay" />
              </div>
              <div className="space-y-1">
                <Label>Paid by (user)</Label>
                <Input value={bill.paid_by_user_id || ""} disabled={locked}
                  onChange={(e) => set({ paid_by_user_id: e.target.value })}
                  placeholder="Team member email or id"
                  data-testid="bill-paid-by" />
              </div>
            </div>

            <div className="pt-2 border-t border-zinc-100" />
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-zinc-700">Line items</div>
              {!locked && (
                <Button variant="outline" size="sm" onClick={addLine}
                  data-testid="bill-add-line">
                  <Plus className="h-4 w-4 mr-1" /> Add line
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 border-b">
                    <th className="p-2">Description</th>
                    <th className="p-2 w-20 text-right">Qty</th>
                    <th className="p-2 w-32 text-right">Unit (ex-GST)</th>
                    <th className="p-2 w-16 text-center">GST</th>
                    <th className="p-2 w-28">Account code</th>
                    <th className="p-2 w-32 text-right">Line total</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {(bill.line_items || []).map((li, i) => {
                    const amt = (Number(li.quantity) || 0) * (Number(li.unit_price) || 0);
                    return (
                      <tr key={li._uid || `l-${i}`} className="border-b" data-testid={`bill-line-${i}`}>
                        <td className="p-2">
                          <Input value={li.description || ""} disabled={locked}
                            onChange={(e) => updateLine(i, { description: e.target.value })} />
                          {li.account_hint && (
                            <div className="text-[10px] text-zinc-400 mt-1">
                              AI hint: {li.account_hint}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          <Input type="number" step="0.01" value={li.quantity}
                            className="text-right" disabled={locked}
                            onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                        </td>
                        <td className="p-2">
                          <Input type="number" step="0.01" value={li.unit_price}
                            className="text-right" disabled={locked}
                            onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                        </td>
                        <td className="p-2 text-center">
                          <Checkbox checked={!!li.gst} disabled={locked}
                            onCheckedChange={(v) => updateLine(i, { gst: !!v })} />
                        </td>
                        <td className="p-2">
                          <Input value={li.account_code || ""} disabled={locked}
                            placeholder={bill.ap_account_code || "—"}
                            onChange={(e) => updateLine(i, { account_code: e.target.value })} />
                        </td>
                        <td className="p-2 text-right">{fmtMoney(amt)}</td>
                        <td className="p-2 text-right">
                          {!locked && (bill.line_items || []).length > 1 && (
                            <Button variant="ghost" size="icon"
                              onClick={() => removeLine(i)}
                              data-testid={`bill-line-${i}-remove`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!(bill.line_items || []).length && (
                    <tr><td colSpan={7} className="p-4 text-center text-zinc-400">
                      No line items — add one to build the bill.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end pt-2">
              <div className="text-sm space-y-1 min-w-[220px]">
                <div className="flex justify-between text-zinc-500">
                  <span>Subtotal (ex-GST)</span><span>{fmtMoney(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-zinc-500">
                  <span>GST</span><span>{fmtMoney(totals.gst_total)}</span>
                </div>
                <div className="flex justify-between font-semibold text-zinc-800 border-t pt-1">
                  <span>Total (inc GST)</span><span>{fmtMoney(totals.total)}</span>
                </div>
                {bill.status === "pushed" && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Balance due</span><span>{fmtMoney(bill.balance_due)}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="text-sm font-semibold text-zinc-700">Xero destination</div>
              <div className="space-y-1">
                <Label>Xero organisation</Label>
                <Select value={bill.xero_tenant_id || "__none__"}
                  disabled={locked}
                  onValueChange={(v) => set({
                    xero_tenant_id: v === "__none__" ? "" : v,
                    xero_tenant_name:
                      tenants.find((t) => t.tenant_id === v)?.tenant_name || "",
                  })}>
                  <SelectTrigger data-testid="bill-tenant-select">
                    <SelectValue placeholder="Select organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Not set —</SelectItem>
                    {tenants.map((t) => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        {t.tenant_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Default AP account code (for this bill)</Label>
                <Input value={bill.ap_account_code || ""} disabled={locked}
                  placeholder="e.g. 800"
                  onChange={(e) => set({ ap_account_code: e.target.value })}
                  data-testid="bill-ap-account-code" />
                <p className="text-[11px] text-zinc-400">
                  Per-line codes (if set) take precedence.
                </p>
              </div>
              {bill.xero_pushed && bill.xero_bill_id && (
                <p className="text-xs text-sky-700 break-all">
                  Xero bill id: {bill.xero_bill_id}
                </p>
              )}
              {bill.xero_push_error && (
                <p className="text-xs text-red-700 break-words">
                  {bill.xero_push_error}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="text-sm font-semibold text-zinc-700">Source document</div>
              {bill.source_document?.original_name ? (
                <>
                  <div className="text-xs text-zinc-500 break-all">
                    <FileText className="h-3.5 w-3.5 inline-block mr-1" />
                    {bill.source_document.original_name}
                  </div>
                  <div className="flex gap-2">
                    <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/bills/${bill.bill_id}/document`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#002FA7] underline"
                      data-testid="bill-download-source">
                      <Download className="h-3.5 w-3.5" /> Open
                    </a>
                    <Button variant="outline" size="sm" onClick={reocr}
                      disabled={reocring || locked}
                      data-testid="bill-reocr-btn">
                      {reocring
                        ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Running</>
                        : <><ScanLine className="h-3.5 w-3.5 mr-1" />Re-run OCR</>}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-xs text-zinc-400">Manually created — no document attached.</div>
              )}
              <div className="text-[11px] text-zinc-400">
                Source: {bill.source || "manual"} · Created {(bill.created_at || "").slice(0, 10)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-2">
              <div className="text-sm font-semibold text-zinc-700">Actions</div>
              {!locked && (
                <Button className="w-full" onClick={save} disabled={saving}
                  data-testid="bill-save-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving</>
                          : <><Save className="h-4 w-4 mr-1" />Save changes</>}
                </Button>
              )}
              {!locked && (
                <Button className="w-full bg-[#002FA7] hover:bg-[#002080]"
                  onClick={push} disabled={pushing}
                  data-testid="bill-push-xero-btn">
                  {pushing
                    ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Pushing</>
                    : <><Send className="h-4 w-4 mr-1" />Push to Xero (ACCPAY)</>}
                </Button>
              )}
              {bill.status === "pushed" && (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setPayOpen(true)}
                  data-testid="bill-record-payment-btn">
                  <DollarSign className="h-4 w-4 mr-1" /> Record payment
                </Button>
              )}
              {bill.status === "paid" && (
                <div className="flex items-center gap-2 text-emerald-700 text-sm justify-center">
                  <CheckCircle2 className="h-4 w-4" /> Fully paid
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={load}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
              {!locked && (
                <Button variant="ghost" size="sm"
                  className="w-full text-red-600 hover:bg-red-50"
                  onClick={remove}
                  data-testid="bill-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete bill
                </Button>
              )}
            </CardContent>
          </Card>

          {(bill.payments || []).length > 0 && (
            <Card>
              <CardContent className="p-5 space-y-2">
                <div className="text-sm font-semibold text-zinc-700">Payments</div>
                {(bill.payments || []).map((p, i) => (
                  <div key={p.payment_id || p.xero_payment_id || `p-${i}`} className="text-xs text-zinc-600 border-b py-1">
                    <div className="flex justify-between">
                      <span>{(p.paid_at || "").slice(0, 10)}</span>
                      <span className="font-medium">{fmtMoney(p.amount)}</span>
                    </div>
                    <div className="text-[11px] text-zinc-400">
                      {(p.method || "").toUpperCase()} · {p.reference || "—"}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <RecordPaymentDialog open={payOpen} onOpenChange={setPayOpen}
        bill={bill} onDone={load} />
    </div>
  );
}

function RecordPaymentDialog({ open, onOpenChange, bill, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("eft");
  const [ref, setRef] = useState("");
  const [payCode, setPayCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(String(bill.balance_due || bill.total || ""));
      setRef(bill.invoice_number || "");
      setPayCode("");
    }
  }, [open, bill]);

  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await api.post(`/bills/${bill.bill_id}/mark-paid`, {
        amount: amt, method, reference: ref || null,
        payment_account_code: payCode || null,
      });
      toast.success("Payment recorded in Xero");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bill-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Amount (AUD)</Label>
            <Input type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="bill-payment-amount" />
          </div>
          <div className="space-y-1">
            <Label>Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="eft">EFT</SelectItem>
                <SelectItem value="direct_debit">Direct debit</SelectItem>
                <SelectItem value="card">Credit card</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Reference</Label>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Xero bank/payment account code</Label>
            <Input value={payCode} onChange={(e) => setPayCode(e.target.value)}
              placeholder="e.g. 090 (leave blank to use default)"
              data-testid="bill-payment-account-code" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
            data-testid="bill-payment-submit">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Recording</>
                    : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
